import type { ShiftEvent, ShiftRQSchema } from '@libs/data-access/queries'
import { Badge } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import classnames from 'classnames'
import React from 'react'
import type { FC } from 'react'

import { getBreaksFromEvents } from '../../utils'
import { BreakCol } from '../BreakCol/BreakCol'
import { ControlBadges } from '../ControlBadges/ControlBadges'
import { hasMealsToShow, MealCol } from '../MealCol/MealCol'
import { Note } from '../Note/Note'
import styles from '../styles/ShiftRealHoursCardsStyle.module.css'

type Props = {
  shift: ShiftRQSchema
  shiftIsFinishedWithoutOut: boolean
  clockInEvent?: ShiftEvent
  clockOutEvent?: ShiftEvent
  hasSnapPadBreakEnable: boolean
}
export const PadCard: FC<Props> = ({
  shift,
  shiftIsFinishedWithoutOut,
  clockInEvent,
  clockOutEvent,
  hasSnapPadBreakEnable
}) => {
  const { t, tCommon, tDates } = usePlanningFormTranslation()

  const padBreaks =
    getBreaksFromEvents(
      shift.shift_events,
      shift.shift_breaks,
      shiftIsFinishedWithoutOut
    ) || []

  if (shiftIsFinishedWithoutOut === false && !clockInEvent) {
    return null
  }

  return (
    <div
      className={classnames([styles.BreakRow, styles.BreakSeparator])}
      id='pad-card'
    >
      <div className={styles.TitleCard}>
        {t('shiftRealHoursForm.padCard.title')}
      </div>
      <div className={styles.BreakCol}>
        {clockInEvent ? (
          <>
            <div className={styles.ShiftEvent}>
              <div className={styles.ShiftEventRow}>
                <div className={styles.ShiftEventHours}>
                  <Badge label='IN' color='semantic.green' size='s' />
                  {tDates('dates.shortHourMinute', {
                    date: clockInEvent.event_occurred_at
                  })}
                </div>
                <ControlBadges event={clockInEvent} />
              </div>
              {Boolean(clockInEvent?.note) && (
                <div className={styles.ShiftEventNote}>
                  <Note note={clockInEvent.note} />
                </div>
              )}
            </div>
            <div className={styles.ShiftEvent}>
              <div className={styles.ShiftEventRow}>
                <div
                  className={classnames(styles.ShiftEventHours, {
                    [styles.ShiftEventHoursWaiting]:
                      !shiftIsFinishedWithoutOut && !clockOutEvent,
                    [styles.ShiftFinishNoOUt]: shiftIsFinishedWithoutOut
                  })}
                >
                  <Badge
                    label='OUT'
                    color={
                      clockOutEvent || shiftIsFinishedWithoutOut
                        ? 'semantic.red'
                        : 'neutral'
                    }
                    size='s'
                  />
                  <span
                    className={!clockOutEvent ? styles.TextItalic : undefined}
                  >
                    {shiftIsFinishedWithoutOut && tCommon('emptyState.none')}
                    {!shiftIsFinishedWithoutOut &&
                      (clockOutEvent
                        ? tDates('dates.shortHourMinute', {
                            date: clockOutEvent.event_occurred_at
                          })
                        : t('shiftRealHoursForm.padCard.waiting'))}
                  </span>
                </div>
                {clockOutEvent && <ControlBadges event={clockOutEvent} />}
              </div>
              {Boolean(clockOutEvent?.note) && (
                <div className={styles.ShiftEventNote}>
                  <Note note={clockOutEvent?.note ?? ''} />
                </div>
              )}
            </div>
            {hasSnapPadBreakEnable && (
              <div className={styles.CardSubPart}>
                <BreakCol
                  breaks={padBreaks}
                  size={padBreaks.length}
                  type='real'
                  cardType='pad'
                  shiftIsFinishedWithoutOut={shiftIsFinishedWithoutOut}
                />
              </div>
            )}
            {shift && hasMealsToShow(shift?.shift_meals, 'clocked') && (
              <div className={styles.CardSubPart}>
                <MealCol meals={shift.shift_meals} scheduleType='clocked' />
              </div>
            )}
          </>
        ) : (
          <span className={styles.HasNoPadCard}>
            {tCommon('emptyState.none')}
          </span>
        )}
      </div>
    </div>
  )
}
